import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Hash, MoreHorizontal } from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '@/store/appStore';
import { InlineEditInput } from './InlineEditInput';
import { handleError } from '@/utils/errorHandler';

interface SortableListItemProps {
  list: { name: string; taskCount: number };
  listId: string;
  spaceName: string;
  folderName: string | null;
  isActive: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  isEditing?: boolean;
  onCancel?: () => void;
}

export function SortableListItem({
  list,
  listId,
  spaceName,
  isActive,
  onClick,
  onContextMenu,
}: SortableListItemProps) {
  const editingItems = useAppStore((state) => state.editingItems);
  const savePendingItemName = useAppStore((state) => state.savePendingItemName);
  const setEditingItem = useAppStore((state) => state.setEditingItem);
  const isEditing = editingItems.has(listId);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `list-${listId}`,
    data: {
      type: 'list',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isEditing ? {} : listeners)} // Don't apply drag listeners when editing
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={clsx(
        'w-full flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] transition-colors cursor-grab active:cursor-grabbing select-none group',
        isActive
          ? 'bg-accent-violet-dim text-accent-violet'
          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
      )}
    >
      <Hash className="w-2.5 h-2.5 shrink-0" />
      {isEditing ? (
        <InlineEditInput
          value={list.name}
          placeholder="Nome lista..."
          onSave={async (name) => {
            try {
              await savePendingItemName(listId, name);
            } catch (error) {
              handleError(error, { level: 'error', message: 'Errore nel salvataggio del nome della lista' });
              setEditingItem(listId, false);
            }
          }}
          onCancel={() => {
            setEditingItem(listId, false);
            // Remove temporary item if cancelled
            const currentSpaces = useAppStore.getState().spaces;
            const updatedSpaces = currentSpaces.map(space => {
              if (space.name !== spaceName) return space;
              return {
                ...space,
                folders: space.folders.map(folder => ({
                  ...folder,
                  lists: folder.lists.filter(l => l.id !== listId),
                })),
              };
            });
            useAppStore.getState().setSpaces(updatedSpaces);
          }}
          className="flex-1"
        />
      ) : (
        <span className="flex-1 text-left truncate cursor-pointer">{list.name}</span>
      )}
      {/* Count / Menu — menu replaces count on hover */}
      {!isEditing && (
        <span className="relative flex items-center justify-end min-w-[16px]">
          <span className="text-[10px] text-text-quaternary tabular-nums group-hover:opacity-0 transition-opacity">
            {list.taskCount}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onContextMenu(e);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="absolute inset-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-surface-tertiary transition-all"
          >
            <MoreHorizontal className="w-2.5 h-2.5 text-text-quaternary" />
          </button>
        </span>
      )}
    </div>
  );
}
